import './crud-table.css';
import { Button, Form, Modal, Popconfirm, Table, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { serverFetch } from '@server';
import { showError } from '@extensions/utils';
import ResizableDrawer from '@controls/resizable-drawer/resizable-drawer';

// Универсальная CRUD-таблица для справочников.
// props: title, api (например 'department'), columns, renderForm(form) -> ReactNode полей формы,
//        queryParams, mapToForm(record) -> values для формы, mapFromForm(values, record) -> body,
//        onChanged() -> вызывается после сохранения/удаления,
//        rowSelection — проп Table для выбора строк, reloadKey — при смене перезагружает данные,
//        infinite — размер порции: без пагинации, строки подгружаются при прокрутке,
//        filterItems(items) -> items — клиентская фильтрация перед отрисовкой,
//        toolbar — ReactNode второй строкой под шапкой,
//        onLoaded(items) -> вызывается после успешной загрузки,
//        drawer — карточка в правой панели с ресайзом по левому краю, modalWidth — её ширина по умолчанию
const CrudTable = ({
    title, api, columns, renderForm, queryParams = {}, mapToForm, mapFromForm,
    extraButtons, rowSelection, reloadKey, infinite, filterItems, toolbar, onLoaded, onChanged,
    modalWidth, drawer,
}) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [visible, setVisible] = useState(infinite ?? Infinity);
    const sentinelRef = useRef(null);
    const [form] = Form.useForm();

    const load = useCallback(() => {
        setLoading(true);
        const qs = Object.entries(queryParams)
            .filter(([, v]) => v != null)
            .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
        serverFetch(api + (qs ? `?${qs}` : ''))
            .then((data) => { setItems(data); onLoaded?.(data); })
            .catch(showError)
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, JSON.stringify(queryParams), reloadKey]);

    useEffect(() => { load(); }, [load]);

    const filtered = filterItems ? filterItems(items) : items;
    const total = filtered.length;
    const shown = infinite ? filtered.slice(0, visible) : filtered;

    useEffect(() => { setVisible(infinite ?? Infinity); }, [items, infinite]);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || !infinite) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) setVisible((v) => Math.min(v + infinite, total));
        }, { rootMargin: '200px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [total, infinite]);

    const openAdd = () => {
        setEditing(null);
        form.resetFields();
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditing(record);
        form.resetFields();
        form.setFieldsValue(mapToForm ? mapToForm(record) : record);
        setModalOpen(true);
    };

    const onDelete = (record) => {
        serverFetch(`${api}/${record.id}`, { method: 'DELETE' })
            .then(() => { load(); onChanged?.(); })
            .catch(showError);
    };

    const onSave = () => {
        form.validateFields().then((values) => {
            const body = mapFromForm ? mapFromForm(values, editing) : { ...(editing ?? {}), ...values };
            const isEdit = editing && editing.id > 0;
            return serverFetch(isEdit ? `${api}/${editing.id}` : api, {
                method: isEdit ? 'PUT' : 'POST',
                bodyData: body,
            }).then(() => {
                setModalOpen(false);
                load();
                onChanged?.();
            });
        }).catch((e) => {
            // Ошибка полей формы — её показывает сам Form. Объект без catch даёт оверлей «[object Object]»
            if (e?.errorFields) return;
            showError(e);
        });
    };

    const cols = [
        ...columns,
        {
            title: '',
            width: 48,
            align: 'right',
            render: (_, record) => (
                <span onClick={(e) => e.stopPropagation()}>
                    <Popconfirm title="Удалить?" onConfirm={() => onDelete(record)} okText="Да" cancelText="Нет">
                        <Button size="small" type="text" danger className="crud-table-delete" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </span>
            ),
        },
    ];

    return (
        <div>
            <div className="crud-table-header">
                {title ? <h3>{title}</h3> : null}
                <Tooltip title="Добавить">
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} />
                </Tooltip>
                {extraButtons}
                <span className="crud-table-total">Всего: {total}</span>
            </div>
            {toolbar}
            <Table
                size="small"
                rowKey="id"
                className="crud-table"
                loading={loading}
                rowSelection={rowSelection}
                dataSource={shown}
                columns={cols}
                onRow={(record) => ({ onClick: () => openEdit(record) })}
                pagination={infinite ? false : { pageSize: 20, showSizeChanger: true, hideOnSinglePage: true }}
            />
            {infinite && (
                <div ref={sentinelRef} className="card-list-sentinel">{visible < total ? 'Загрузка…' : null}</div>
            )}
            {drawer ? (
                <ResizableDrawer
                    open={modalOpen}
                    storageKey={`${api}DrawerWidth`}
                    defaultWidth={modalWidth || 760}
                    title={editing?.id ? 'Изменить' : 'Добавить'}
                    onClose={() => setModalOpen(false)}
                    destroyOnClose
                    styles={{ body: { display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' } }}
                >
                    <div className="crud-drawer-scroll">
                        <Form form={form} layout="vertical">
                            {renderForm(form, editing)}
                        </Form>
                    </div>
                    <div className="crud-drawer-actions">
                        <Button type="primary" onClick={onSave}>Сохранить</Button>
                        <Button onClick={() => setModalOpen(false)}>Отмена</Button>
                    </div>
                </ResizableDrawer>
            ) : (
                <Modal
                    open={modalOpen}
                    title={editing ? 'Изменить' : 'Добавить'}
                    onOk={onSave}
                    onCancel={() => setModalOpen(false)}
                    okText="Сохранить"
                    cancelText="Отмена"
                    width={modalWidth}
                    destroyOnClose
                >
                    <Form form={form} layout="vertical">
                        {renderForm(form, editing)}
                    </Form>
                </Modal>
            )}
        </div>
    );
};

export default CrudTable;
